import type { Icon } from "@phosphor-icons/react";

import { cn } from "@/lib/utils";

interface OnboardingOptionRowProps {
  label: string;
  /** Leading glyph, from the group's `icon` — @see `resolveAccessibilityIcon`. */
  icon?: Icon;
  selected: boolean;
  /** Shared across the group, so arrow keys move between the rows. */
  name: string;
  onSelect: () => void;
}

/**
 * Single-choice row for the access-needs step: leading glyph, label, trailing
 * radio (native's `OnboardingOptionRow`, Figma "Selectable list item/Large-48").
 *
 * A **radio**, not a checkbox — the step takes one answer, and picking a row
 * replaces the previous pick. The whole row is the target, via the same real
 * `<input type="radio">` the choice cards use.
 *
 * The visible radio is drawn rather than left to the browser so it can carry
 * the design's 20pt ring and 12pt dot. It keeps its 1.5px border in both
 * states — only the colour changes — so selecting a row cannot nudge the label.
 */
const OnboardingOptionRow = ({
  label,
  icon: IconComponent,
  selected,
  name,
  onSelect,
}: OnboardingOptionRowProps) => (
  <label
    className={cn(
      "flex min-h-[48px] cursor-pointer items-center gap-2 rounded-lg px-4 py-3 transition-colors",
      "has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-focusRing-default",
      selected
        ? "border-[1.5px] border-border-token-brand-subtle bg-bg-brand-tertiary-default"
        : "border border-border-token-brand-minimal bg-bg-brand-secondary-default hover:bg-bg-brand-secondary-hover",
    )}
  >
    <input
      type="radio"
      name={name}
      checked={selected}
      onChange={onSelect}
      className="sr-only"
    />

    {IconComponent ? (
      <IconComponent
        size={24}
        aria-hidden="true"
        className="shrink-0 text-fg-neutral-strong"
      />
    ) : null}

    {/* `flex-1` lets long labels wrap instead of pushing the radio off the row
        when the user scales text up. */}
    <span className="body-base-medium flex-1 px-1 text-fg-neutral-strong">
      {label}
    </span>

    {/* Decorative: the input's own checked state already conveys this. */}
    <span
      aria-hidden="true"
      className="flex min-w-[32px] shrink-0 items-center justify-end"
    >
      <span
        className={cn(
          "flex h-5 w-5 items-center justify-center rounded-full border-[1.5px] bg-bg-brand-secondary-default",
          selected
            ? "border-border-token-brand-subtle"
            : "border-border-token-neutral-strong",
        )}
      >
        {selected ? (
          <span className="h-3 w-3 rounded-full bg-fg-brand-subtle" />
        ) : null}
      </span>
    </span>
  </label>
);

export default OnboardingOptionRow;
